import { useQuery, useMutation } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Activity, CheckCircle2, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface SyncHealthStats {
  unprocessedCount: number;
  processedCount: number;
  lastAcknowledgedAt: string | null;
  oldestUnprocessedAt: string | null;
  status: "healthy" | "warning" | "critical";
  oldestUnprocessedAgeMinutes: number;
}

interface ReadOnlyDropboxStatus {
  configured: boolean;
  authorized: boolean;
  connected: boolean;
  needsReauthorization?: boolean;
  error?: string;
}

export function SyncHealthMonitor() {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<SyncHealthStats>({
    queryKey: ["/api/admin/sync/health"],
    refetchInterval: 30000,
  });

  // Loud warning when the read-only Dropbox connection was set up before
  // but is now dead (e.g. credentials wiped by a database restore).
  const { data: roDropbox } = useQuery<ReadOnlyDropboxStatus>({
    queryKey: ["/api/dropbox-readonly/status"],
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
  const roDropboxBroken =
    !!roDropbox &&
    (roDropbox.needsReauthorization === true ||
      (roDropbox.authorized && !roDropbox.connected));

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/sync/refresh-all");
      return res.json();
    },
    onSuccess: (data: { eventsQueued: number }) => {
      toast({ title: `Queued ${data.eventsQueued} sync events`, description: "All clients, projects, and budget items have been re-sent." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sync/health"] });
    },
    onError: (error: Error) => {
      toast({ title: "Refresh failed", description: error.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-lg bg-gray-800/50 border border-gray-700 animate-pulse">
            <div className="h-3 w-20 bg-gray-700 rounded mb-2" />
            <div className="h-6 w-12 bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-400 p-3 rounded-lg bg-red-900/20 border border-red-800">
        Failed to load sync health stats
      </div>
    );
  }

  if (!data) return null;

  const pendingColor = data.status === "critical"
    ? "text-red-400"
    : data.status === "warning"
      ? "text-amber-400"
      : data.unprocessedCount > 0
        ? "text-amber-400"
        : "text-emerald-400";

  const pendingBg = data.status === "critical"
    ? "bg-red-900/20 border-red-800/50"
    : data.status === "warning"
      ? "bg-amber-900/20 border-amber-800/50"
      : data.unprocessedCount > 0
        ? "bg-amber-900/20 border-amber-800/50"
        : "bg-emerald-900/20 border-emerald-800/50";

  const lastActivity = data.lastAcknowledgedAt
    ? formatDistanceToNow(new Date(data.lastAcknowledgedAt), { addSuffix: true })
    : "Never";

  return (
    <div className="space-y-3">
      {roDropboxBroken && (
        <div
          className="flex items-center gap-2 p-2 rounded-lg text-sm bg-red-900/30 border border-red-800 text-red-300"
          data-testid="alert-readonly-dropbox-broken"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {roDropbox?.needsReauthorization
              ? "Read-only Dropbox connection lost (likely after a database restore) — re-authorize it in Settings → Read-Only Dropbox Browser."
              : "Read-only Dropbox connection is failing — check Settings → Read-Only Dropbox Browser."}
          </span>
        </div>
      )}
      {data.status !== "healthy" && (
        <div className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
          data.status === "critical"
            ? "bg-red-900/30 border border-red-800 text-red-300"
            : "bg-amber-900/30 border border-amber-800 text-amber-300"
        }`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {data.status === "critical"
              ? `Sync queue stuck: ${data.unprocessedCount} events pending for ${data.oldestUnprocessedAgeMinutes}+ minutes`
              : `Sync queue growing: ${data.unprocessedCount} events pending`
            }
          </span>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className={`p-3 rounded-lg border ${pendingBg}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Activity className={`h-3.5 w-3.5 ${pendingColor}`} />
            <span className="text-xs text-gray-400">Pending Events</span>
          </div>
          <span className={`text-xl font-semibold ${pendingColor}`}>
            {data.unprocessedCount}
          </span>
        </div>

        <div className="p-3 rounded-lg border bg-gray-800/50 border-gray-700">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
            <span className="text-xs text-gray-400">Total Processed</span>
          </div>
          <span className="text-xl font-semibold text-blue-400">
            {data.processedCount}
          </span>
        </div>

        <div className="p-3 rounded-lg border bg-gray-800/50 border-gray-700">
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs text-gray-400">Last Sync Activity</span>
          </div>
          <span className="text-sm font-medium text-gray-300">
            {lastActivity}
          </span>
        </div>
      </div>

      <div className="p-3 rounded-lg border bg-gray-800/50 border-gray-700 mt-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-200">Complete Data Refresh</h4>
            <p className="text-xs text-gray-400 mt-0.5">
              Re-queue all clients, projects, and budget items to the sync queue.
              Clears any pending unprocessed events first.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={refreshMutation.isPending}
            onClick={() => {
              if (window.confirm("This will clear pending sync events and re-send all entities to the Finance App. Continue?")) {
                refreshMutation.mutate();
              }
            }}
            className="ml-4 shrink-0 border-amber-700 text-amber-400 hover:bg-amber-900/30 hover:text-amber-300"
          >
            {refreshMutation.isPending ? (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            {refreshMutation.isPending ? "Sending..." : "Force Resend All"}
          </Button>
        </div>
      </div>
    </div>
  );
}
